import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SparklesIcon, ChevronLeftIcon, ArrowTopRightOnSquareIcon, CheckCircleIcon, FlagIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  useAssistantSummary, useAssistantCommon, useAssistantUnanswered, useAssistantWeak, useAssistantFlagged,
  useAssistantDocumentChanges, useAssistantGroup, useReviewGroup, useReviewFlag, useAssistantSources, useStopReusing,
  useLearnedWords, useNearMisses, useNearMissDecision, useAssistantAllQuestions,
} from '../hooks/useAssistantReview';

// The assistant server returns MySQL DATETIMEs as "YYYY-MM-DD HH:MM:SS".
const toDate = (d) => (d ? new Date(String(d).replace(' ', 'T')) : null);
const fmtDay = (d) => (d ? toDate(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const fmtTime = (d) => (d ? toDate(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const score = (s) => (s === null || s === undefined ? '–' : s.toFixed(2));

const PERIODS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: 'All time', days: null },
];

const TABS = [
  { key: 'all', label: 'All questions' },
  { key: 'common', label: 'Common questions' },
  { key: 'unanswered', label: 'Not answered' },
  { key: 'escalated', label: 'Escalated' },
  { key: 'weak', label: 'Wording mismatches' },
  { key: 'flagged', label: 'Flagged as incorrect' },
  { key: 'changes', label: 'Document changes' },
  { key: 'sources', label: 'API vs pool' },
  { key: 'near', label: 'Near misses' },
];

const CARD = 'rounded-lg border border-gray-200 bg-white';
const EMPTY = 'px-4 py-8 text-center text-sm text-gray-400';
// Toggle buttons used by the answer-pool lists (filter chips, Same/Different).
const CHIP = 'inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50';
const CHIP_ON = 'inline-flex items-center justify-center gap-1.5 rounded-md border border-brand bg-brand px-3 py-1.5 text-sm font-medium text-white';

function Loading({ q }) {
  if (q.isLoading) return <p className="px-4 py-6 text-sm text-gray-400">Loading…</p>;
  if (q.isError) return <p className="px-4 py-6 text-sm text-red-600">{q.error?.response?.data?.error || 'Could not load this list.'}</p>;
  return null;
}

// With onClick it's a button that opens the matching list below; `active`
// marks the list currently shown.
function Tile({ label, value, hint, alert, onClick, active }) {
  const body = (
    <>
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${alert ? 'text-amber-600' : 'text-gray-900'}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-gray-400">{hint}</div>}
    </>
  );
  if (!onClick) return <div className={`${CARD} px-4 py-3`}>{body}</div>;
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={`${CARD} px-4 py-3 text-left transition hover:border-brand hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${active ? 'border-brand ring-1 ring-brand' : ''}`}>
      {body}
    </button>
  );
}

function ReviewedBadge({ at, by }) {
  if (!at) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700" title={by || ''}>
      <CheckCircleIcon className="h-3.5 w-3.5" /> Reviewed {fmtDay(at)}
    </span>
  );
}

// Optional note + confirm. Records who reviewed it and when.
function ReviewForm({ mutation, id, label = 'Mark reviewed', placeholder }) {
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
        <CheckCircleIcon className="h-4 w-4" /> {label}
      </button>
    );
  }
  const onSubmit = async (e) => {
    e.preventDefault();
    await mutation.mutateAsync({ id, note: note.trim() || null });
    setOpen(false);
    setNote('');
  };
  return (
    <form onSubmit={onSubmit} className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-start">
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={placeholder || 'Note (optional) — what you checked or changed'}
        className="min-h-[42px] flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
      <div className="flex gap-2">
        <button type="submit" disabled={mutation.isPending}
          className="rounded-lg bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-40">Save</button>
        <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-100">Cancel</button>
      </div>
      {mutation.isError && <p className="text-sm text-red-600">{mutation.error?.response?.data?.error || 'Could not save.'}</p>}
    </form>
  );
}

// Every time a group of questions was asked: the answer given and how it was found.
function GroupPanel({ id, onClose }) {
  const q = useAssistantGroup(id);
  const reviewGroup = useReviewGroup();
  const g = q.data;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-2xl flex-col bg-gray-50 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Question group</div>
            <h2 className="text-base font-semibold text-gray-900">{g?.question || '…'}</h2>
            {g && <p className="text-xs text-gray-400">Asked {g.count}× · first {fmtDay(g.firstAsked)} · last {fmtDay(g.lastAsked)}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100" aria-label="Close"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <Loading q={q} />
          {g && (
            <div className={`${CARD} p-3`}>
              <div className="flex flex-wrap items-center gap-2">
                <ReviewedBadge at={g.reviewedAt} by={g.reviewedBy} />
                {g.reviewedAt && <span className="text-xs text-gray-500">by {g.reviewedBy}{g.reviewNote ? ` — ${g.reviewNote}` : ''}</span>}
              </div>
              <ReviewForm mutation={reviewGroup} id={g.id} label={g.reviewedAt ? 'Review again' : 'Mark answers reviewed'} />
            </div>
          )}
          {g?.entries.map((e) => (
            <div key={e.id} className={`${CARD} p-3`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-900">“{e.question}”</p>
                <span className="text-xs text-gray-400">{fmtTime(e.askedAt)}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
                {!e.answered && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700">Not answered</span>}
                {e.escalated && <span className="rounded-full bg-blue-100 px-2 py-0.5 font-medium text-blue-700">Escalated</span>}
                {e.flagged && <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-700">Flagged{e.flagReviewed ? ' (reviewed)' : ''}</span>}
                {e.answerMode === 'pool' && <span className="rounded-full bg-brand-light px-2 py-0.5 font-medium text-brand">From pool</span>}
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">Search score {score(e.topScore)}</span>
                {e.expansionApplied && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">Wording expanded</span>}
                {e.costUsd !== null && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">${e.costUsd.toFixed(4)}</span>}
              </div>
              {e.answer && <p className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-sm text-gray-700">{e.answer}</p>}
              {e.flaggedNote && <p className="mt-1 text-xs text-red-600">Flag note: {e.flaggedNote}</p>}
              <div className="mt-2 text-xs text-gray-500">
                <span className="font-medium">Cited:</span> {e.cited?.length ? e.cited.map((c) => c.title).join('; ') : 'nothing'}
              </div>
              {e.retrieved?.length > 0 && (
                <details className="mt-1 text-xs text-gray-500">
                  <summary className="cursor-pointer">How it searched ({e.retrieved.length} passages)</summary>
                  <ul className="mt-1 space-y-0.5 pl-4">
                    {e.retrieved.map((r) => <li key={r.chunkId}>{score(r.score)} — {r.title}</li>)}
                  </ul>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// escalatedOnly: the Escalated tab — same list, only questions that got the
// safety escalation message.
function CommonTab({ days, openGroup, escalatedOnly = false }) {
  const q = useAssistantCommon(days, escalatedOnly);
  return (
    <div className={CARD}>
      {escalatedOnly && (
        <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
          Questions treated as safety-critical — the engineer was told to confirm with their Supervisor, Installation Manager or SHEQ before proceeding.
        </p>
      )}
      <Loading q={q} />
      {q.data?.length === 0 && <p className={EMPTY}>{escalatedOnly ? 'No escalated questions in this period.' : 'No questions in this period yet.'}</p>}
      <div className="divide-y divide-gray-100">
        {q.data?.map((g) => (
          <button key={g.id} type="button" onClick={() => openGroup(g.id)} className="block w-full px-4 py-3 text-left hover:bg-brand-light/40">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{g.question}</p>
                {g.alsoAsked.length > 0 && <p className="mt-0.5 truncate text-xs text-gray-400">Also: {g.alsoAsked.map((v) => `“${v}”`).join(' · ')}</p>}
                {g.sources.length > 0 && <p className="mt-0.5 truncate text-xs text-gray-500">Sources: {g.sources.map((s) => s.title).join('; ')}</p>}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="rounded-full bg-brand-light px-2 py-0.5 text-xs font-semibold text-brand">{g.asked}×</span>
                <ReviewedBadge at={g.reviewedAt} by={g.reviewedBy} />
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Answered {g.answered}/{g.asked}{g.escalated ? ` · escalated ${g.escalated}` : ''} · search score {score(g.avgScore)} · last {fmtDay(g.lastAsked)}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function UnansweredTab({ days, openGroup }) {
  const q = useAssistantUnanswered(days);
  return (
    <div className={CARD}>
      <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
        The assistant had nothing in the documents to answer these. A missing or unclear procedure is the usual cause.
      </p>
      <Loading q={q} />
      {q.data?.length === 0 && <p className={EMPTY}>Nothing unanswered in this period.</p>}
      <div className="divide-y divide-gray-100">
        {q.data?.map((m) => (
          <button key={m.groupId} type="button" onClick={() => openGroup(m.groupId)} className="block w-full px-4 py-3 text-left hover:bg-brand-light/40">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium text-gray-900">{m.question}</p>
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">{m.asked}×</span>
            </div>
            <p className="mt-1 text-xs text-gray-400">Closest document: {m.closestDocument || '–'} (score {score(m.bestScore)}) · last {fmtDay(m.lastAsked)}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function WeakTab({ days }) {
  const q = useAssistantWeak(days);
  return (
    <div className={CARD}>
      <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
        Answered, but the search only found weak matches — usually engineers asking in different words from the document. Worth adding their phrasing to the assistant's wording list.
      </p>
      <Loading q={q} />
      {q.data?.length === 0 && <p className={EMPTY}>No weak matches in this period.</p>}
      <div className="divide-y divide-gray-100">
        {q.data?.map((w) => (
          <div key={w.question} className="flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm text-gray-900">“{w.question}”</p>
              <p className="mt-0.5 text-xs text-gray-400">Best match: {w.bestDocument} · last {fmtDay(w.lastAsked)}</p>
            </div>
            <div className="shrink-0 text-right text-xs text-gray-500">
              <div className="font-semibold text-gray-700">{score(w.topScore)}</div>
              <div>{w.asked}×</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const RETIRED_REASON = {
  document_changed: 'its procedure changed',
  document_removed: 'its procedure was removed',
  document_unavailable: 'its procedure failed to load',
  flagged_incorrect: 'an answer from it was flagged',
  no_citations: 'it cited nothing',
  reviewer_stopped: 'a reviewer stopped it',
};

// Whether a saved answer is still being handed out, and if not, why.
function PoolStatus({ entry }) {
  if (!entry.retiredAt) return <span className="text-emerald-700">In the pool · reused {entry.timesServed}×</span>;
  return (
    <span className="text-amber-700">
      No longer reused since {fmtDay(entry.retiredAt)} — {RETIRED_REASON[entry.retiredReason] || entry.retiredReason}
      {entry.retiredBy ? ` (${entry.retiredBy}${entry.retireNote ? `: ${entry.retireNote}` : ''})` : ''}
    </span>
  );
}

// Pool answers: the original API question and answer next to this one, so a
// reviewer can judge whether reusing it was right.
function SourceRow({ r, openGroup }) {
  const [open, setOpen] = useState(false);
  const stopReusing = useStopReusing();
  const o = r.original;
  // The saved answer this row reused (pool) or became (API).
  const entry = o || r.saved;
  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-sm font-medium text-gray-900">“{r.question}”</p>
        {r.source === 'pool' ? (
          <span className="shrink-0 rounded-full bg-brand-light px-2 py-0.5 text-[11px] font-semibold text-brand">Pool</span>
        ) : (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">API</span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-gray-500">
        <span>{fmtTime(r.askedAt)}</span>
        {r.source === 'api' && r.costUsd !== null && <span>· ${r.costUsd.toFixed(4)}</span>}
        {o && <span>· Match {score(o.matchScore)}</span>}
        {r.escalated && <span className="text-blue-700">· Escalated</span>}
        {r.flagged && <span className="text-red-700">· Flagged</span>}
      </div>
      {entry && <p className="mt-1 text-[11px]"><PoolStatus entry={entry} /></p>}

      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={() => setOpen(!open)} className="text-xs font-semibold text-brand">
          {open ? 'Hide' : r.source === 'pool' ? 'Compare with original' : 'Show answer'}
        </button>
        {r.groupId && (
          <button type="button" onClick={() => openGroup(r.groupId)} className="text-xs font-semibold text-gray-500">
            Question group
          </button>
        )}
      </div>

      {open && (
        <div className="mt-2 space-y-2">
          {o && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Original API question · {fmtDay(o.askedAt)}</div>
              <p className="mt-0.5 text-sm font-medium text-gray-900">“{o.question}”</p>
              {o.matchedKeywords?.length > 0 && (
                <p className="mt-1 text-[11px] text-gray-600">
                  <span className="font-semibold">Matched on:</span> {o.matchedKeywords.join(', ')}
                  {o.originalOnlyKeywords?.length > 0 && <span className="text-gray-400"> · original also had: {o.originalOnlyKeywords.join(', ')}</span>}
                </p>
              )}
              {o.flagged && <p className="mt-1 text-[11px] text-red-700">Original flagged as incorrect</p>}
              {o.answer && <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{o.answer}</p>}
            </div>
          )}
          <div className="rounded-md bg-gray-50 p-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{o ? 'Answer given from the pool' : 'Answer'}</div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{r.answer}</p>
            {r.flaggedNote && <p className="mt-1 text-xs text-red-600">Flag note: {r.flaggedNote}</p>}
          </div>
        </div>
      )}

      {/* Takes the saved answer out of the pool — nobody gets it again; the
          next person to ask gets a fresh API answer, which is saved instead. */}
      {entry && !entry.retiredAt && (
        <div className="mt-2">
          <ReviewForm mutation={stopReusing} id={entry.poolId} label="Stop reusing this answer"
            placeholder="Why — e.g. matched a different question, answer incomplete" />
        </div>
      )}
    </div>
  );
}

// Words the pool taught itself: two wordings got the same answer and differed
// by one word each. Forgotten when an answer they came from is flagged or
// stopped.
function LearnedWords() {
  const q = useLearnedWords();
  const [open, setOpen] = useState(false);
  const live = (q.data || []).filter((w) => w.inUse);
  if (!q.data?.length) return null;
  return (
    <div className={`${CARD} px-4 py-3`}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left">
        <span className="text-sm font-semibold text-gray-900">Words the pool has learned</span>
        <span className="text-xs text-gray-500">{live.length} · {open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <ul className="mt-2 space-y-2">
          {q.data.map((w) => (
            <li key={w.id} className={`text-xs ${w.forgottenAt ? 'text-gray-400' : 'text-gray-700'}`}>
              <span className={`font-semibold ${w.forgottenAt ? 'line-through' : ''}`}>“{w.keyword}” = “{w.sameAs}”</span>
              <span className="text-gray-400"> · {fmtDay(w.learnedAt)}</span>
              {w.forgottenAt && <span> · forgotten — {RETIRED_REASON[w.forgottenReason] || w.forgottenReason}</span>}
              {!w.forgottenAt && !w.inUse && <span className="text-amber-700"> · seen once — used after a second example</span>}
              {w.learnedFrom && w.matched && <div className="text-gray-400">from “{w.learnedFrom}” and “{w.matched}”</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Questions the API answered that came close to a saved one. Optional — the
// pool learns on its own — but one tap here teaches it straight away.
function NearMissesTab({ days }) {
  const q = useNearMisses(days);
  const decide = useNearMissDecision();
  return (
    <div className={CARD}>
      <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
        These went to the API but look close to a question already in the pool. <span className="font-semibold">Same question</span> teaches the pool this wording, so it's answered from the pool next time. <span className="font-semibold">Different</span> keeps them apart. Optional — the pool keeps learning either way.
      </p>
      <Loading q={q} />
      {q.data?.length === 0 && <p className={EMPTY}>No near misses in this period.</p>}
      <div className="divide-y divide-gray-100">
        {q.data?.map((m) => (
          <div key={m.questionLogId} className="px-4 py-3">
            <p className="text-sm font-medium text-gray-900">“{m.question}”</p>
            <p className="text-[11px] text-gray-400">{fmtTime(m.askedAt)} · went to the API</p>
            <div className="mt-2 rounded-md bg-gray-50 p-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Looks like</div>
              <p className="text-sm text-gray-800">“{m.saved.question}”</p>
              <p className="mt-1 text-[11px] text-gray-600">
                <span className="font-semibold">Shared:</span> {m.shared.join(', ') || '—'}
                {m.onlyNew.length > 0 && <span> · <span className="font-semibold">only in this one:</span> {m.onlyNew.join(', ')}</span>}
                {m.onlySaved.length > 0 && <span className="text-gray-400"> · only in the saved one: {m.onlySaved.join(', ')}</span>}
              </p>
            </div>
            <div className="mt-2 flex gap-2">
              {['same', 'different'].map((decision) => (
                <button key={decision} type="button" disabled={decide.isPending}
                  onClick={() => decide.mutate({ questionLogId: m.questionLogId, poolId: m.saved.poolId, decision })}
                  className={`${decision === 'same' ? CHIP_ON : CHIP} disabled:opacity-40`}>
                  {decision === 'same' ? 'Same question' : 'Different'}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {decide.isError && <p className="px-4 pb-3 text-sm text-red-600">{decide.error?.response?.data?.error || 'Could not save.'}</p>}
    </div>
  );
}

// Every question asked, newest first, whatever answered it: the API, the
// answer pool, or plain retrieval (ANSWER_MODE=retrieval — answers built
// straight from the documents, which API vs pool doesn't list).
const SOURCE_BADGE = {
  api: ['API', 'bg-gray-100 text-gray-700'],
  pool: ['Pool', 'bg-brand-light text-brand'],
  retrieval: ['Documents', 'bg-sky-100 text-sky-700'],
};

function QuestionRow({ r, openGroup }) {
  const [open, setOpen] = useState(false);
  const [label, badge] = SOURCE_BADGE[r.source] || [r.source, 'bg-gray-100 text-gray-700'];
  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-sm font-medium text-gray-900">“{r.question}”</p>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge}`}>{label}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[11px] text-gray-500">
        <span>{fmtTime(r.askedAt)}</span>
        {r.askedBy && <span>· {r.askedBy}</span>}
        {r.app && <span>· {r.app}</span>}
        <span className={r.answered ? '' : 'font-semibold text-amber-700'}>· {r.answered ? 'Answered' : 'Not answered'}</span>
        {r.escalated && <span className="font-semibold text-red-600">· Escalated</span>}
        {r.flagged && <span className="font-semibold text-red-600">· Flagged as incorrect</span>}
        <span>· score {score(r.topScore)}</span>
        {r.costUsd ? <span>· ${r.costUsd.toFixed(3)}</span> : null}
      </div>
      {r.sources.length > 0 && <p className="mt-1 text-[11px] text-gray-500">Sources: {r.sources.join(', ')}</p>}
      <div className="mt-1.5 flex gap-3 text-xs font-medium text-brand">
        {r.answer && (
          <button type="button" onClick={() => setOpen((v) => !v)}>{open ? 'Hide answer' : 'Show answer'}</button>
        )}
        {r.groupId && (
          <button type="button" onClick={() => openGroup(r.groupId)}>Open group</button>
        )}
      </div>
      {open && <p className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-sm text-gray-700">{r.answer}</p>}
    </div>
  );
}

function AllQuestionsTab({ days, openGroup }) {
  const [mode, setMode] = useState('all');
  const q = useAssistantAllQuestions(days, mode);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {[['all', 'All'], ['api', 'API'], ['pool', 'Pool'], ['retrieval', 'Documents']].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setMode(key)} className={mode === key ? CHIP_ON : CHIP}>{label}</button>
        ))}
      </div>
      <div className={CARD}>
        <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
          Every question asked, newest first. API = answered by the AI model, Pool = a saved API answer reused, Documents = built straight from the documents with no AI call (ANSWER_MODE=retrieval).
        </p>
        <Loading q={q} />
        {q.data?.length === 0 && <p className={EMPTY}>No questions in this period.</p>}
        <div className="divide-y divide-gray-100">
          {q.data?.map((r) => <QuestionRow key={r.id} r={r} openGroup={openGroup} />)}
        </div>
      </div>
    </div>
  );
}

function SourcesTab({ days, openGroup }) {
  const [source, setSource] = useState('all');
  const q = useAssistantSources(days, source);
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {[['all', 'All'], ['api', 'API'], ['pool', 'Pool']].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setSource(key)} className={source === key ? CHIP_ON : CHIP}>{label}</button>
        ))}
      </div>
      <LearnedWords />
      <div className={CARD}>
        <p className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
          Which answers came from the API and which were reused from the answer pool. Open a pool answer to compare the question with the original it was matched to — a low match or a different question means the reuse was wrong.
        </p>
        <Loading q={q} />
        {q.data?.length === 0 && <p className={EMPTY}>No answers in this period.</p>}
        <div className="divide-y divide-gray-100">
          {q.data?.map((r) => <SourceRow key={r.id} r={r} openGroup={openGroup} />)}
        </div>
      </div>
    </div>
  );
}

function FlaggedTab() {
  const [status, setStatus] = useState('open');
  const q = useAssistantFlagged(status);
  const reviewFlag = useReviewFlag();
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {['open', 'reviewed'].map((s) => (
          <button key={s} type="button" onClick={() => setStatus(s)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${status === s ? 'bg-brand text-white' : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
            {s === 'open' ? 'To review' : 'Reviewed'}
          </button>
        ))}
      </div>
      <div className={CARD}>
        <Loading q={q} />
        {q.data?.length === 0 && <p className={EMPTY}>{status === 'open' ? 'No flagged answers waiting.' : 'Nothing reviewed yet.'}</p>}
        <div className="divide-y divide-gray-100">
          {q.data?.map((f) => (
            <div key={f.messageId} className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 text-sm font-medium text-gray-900"><FlagIcon className="h-4 w-4 text-red-500" /> “{f.question}”</p>
                <span className="text-xs text-gray-400">{f.askedBy} · {fmtTime(f.askedAt)}</span>
              </div>
              {f.note && <p className="mt-1 text-sm text-red-700">“{f.note}”</p>}
              <p className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-sm text-gray-700">{f.answer}</p>
              <p className="mt-1 text-xs text-gray-500"><span className="font-medium">Cited:</span> {f.cited.length ? f.cited.map((c) => c.title).join('; ') : 'nothing'}</p>
              {f.reviewedAt ? (
                <p className="mt-2 text-xs text-gray-500"><ReviewedBadge at={f.reviewedAt} by={f.reviewedBy} /> by {f.reviewedBy}{f.reviewNote ? ` — ${f.reviewNote}` : ''}</p>
              ) : (
                <div className="mt-2"><ReviewForm mutation={reviewFlag} id={f.messageId} placeholder="What was wrong, and what was done about it" /></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChangesTab({ openGroup }) {
  const q = useAssistantDocumentChanges();
  const reviewGroup = useReviewGroup();
  const changed = q.data?.changed || [];
  const removed = q.data?.removed || [];
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Procedures updated in SharePoint in the last 90 days. Answers given before the update cited the old version — check each group still gets a correct answer, then mark it reviewed.
      </p>
      <div className={CARD}>
        <Loading q={q} />
        {q.data && changed.length === 0 && <p className={EMPTY}>No document changes in the last 90 days.</p>}
        <div className="divide-y divide-gray-100">
          {changed.map((d) => (
            <div key={d.documentId} className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
                  {d.title} <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
                <span className="text-xs text-gray-400">Changed {fmtDay(d.changedAt)}</span>
              </div>
              {d.affectedGroups.length === 0 ? (
                <p className="mt-1 text-xs text-emerald-700">No answers to re-check.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {d.affectedGroups.map((g) => (
                    <li key={g.groupId} className="rounded-md bg-amber-50 p-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <button type="button" onClick={() => openGroup(g.groupId)} className="text-left text-sm text-gray-900 hover:underline">{g.question}</button>
                        <span className="text-xs text-amber-700">{g.answersBeforeChange} answer{g.answersBeforeChange === 1 ? '' : 's'} before the change</span>
                      </div>
                      <ReviewForm mutation={reviewGroup} id={g.groupId} placeholder="Checked against the new revision — note anything that changed" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
      {removed.length > 0 && (
        <div className={CARD}>
          <div className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Removed from SharePoint</div>
          <div className="divide-y divide-gray-100">
            {removed.map((r) => (
              <div key={`${r.documentId}-${r.removedAt}`} className="flex items-center justify-between gap-2 px-4 py-2 text-sm">
                <span className="text-gray-700">{r.title}</span>
                <span className="text-xs text-gray-400">{fmtDay(r.removedAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Support > AI Assistant Review: how the field assistant is being used, what
// it can't answer, what engineers flagged as wrong, and which answers need
// re-checking after a procedure changed. Meets ISO-RA-155's post-release
// review conditions.
function AssistantReview() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState('all');
  const [groupId, setGroupId] = useState(null);
  const s = useAssistantSummary(days);
  const sum = s.data;

  const tabCount = {
    unanswered: sum?.unanswered,
    flagged: sum?.openFlags,
    changes: sum?.groupsNeedingReview,
  };

  return (
    <div className="p-6">
      <button type="button" onClick={() => navigate('/?dept=support')} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Support
      </button>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="mb-1 flex items-center gap-2 text-2xl font-bold text-gray-900">
            <SparklesIcon className="h-7 w-7 text-brand" /> AI Assistant Review
          </h1>
          <p className="text-sm text-gray-500">What engineers ask the assistant, what it couldn't answer, what they flagged as wrong, and answers to re-check after a procedure changes.</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-1">
          {PERIODS.map((p) => (
            <button key={p.label} type="button" onClick={() => setDays(p.days)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${days === p.days ? 'bg-brand text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {s.isError && (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {s.error?.response?.data?.error || 'Could not reach the AI assistant review service.'}
        </p>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Tile label="Questions" value={sum ? sum.questions : '–'} hint={sum ? `${sum.sessions} chats` : ''}
          onClick={() => setTab('common')} active={tab === 'common'} />
        <Tile label="Not answered" value={sum ? sum.unanswered : '–'} alert={sum?.unanswered > 0}
          onClick={() => setTab('unanswered')} active={tab === 'unanswered'} />
        <Tile label="Escalated" value={sum ? sum.escalated : '–'} hint="Safety-critical"
          onClick={() => setTab('escalated')} active={tab === 'escalated'} />
        <Tile label="Flags to review" value={sum ? sum.openFlags : '–'} alert={sum?.openFlags > 0}
          onClick={() => setTab('flagged')} active={tab === 'flagged'} />
        <Tile label="Answers to re-check" value={sum ? sum.groupsNeedingReview : '–'} hint="After a document change" alert={sum?.groupsNeedingReview > 0}
          onClick={() => setTab('changes')} active={tab === 'changes'} />
        <Tile label="Spend" value={sum ? `${sum.costUsd.toFixed(2)}` : '–'} hint={sum ? `${sum.fromApi} from the API · ${sum.fromPool} reused from the pool` : ''}
          onClick={() => setTab('sources')} active={tab === 'sources'} />
      </div>

      <div className="mb-3 flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={`-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${tab === t.key ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
            {tabCount[t.key] > 0 && <span className="rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-700">{tabCount[t.key]}</span>}
          </button>
        ))}
      </div>

      {tab === 'all' && <AllQuestionsTab days={days} openGroup={setGroupId} />}
      {tab === 'common' && <CommonTab days={days} openGroup={setGroupId} />}
      {tab === 'unanswered' && <UnansweredTab days={days} openGroup={setGroupId} />}
      {tab === 'escalated' && <CommonTab days={days} openGroup={setGroupId} escalatedOnly />}
      {tab === 'weak' && <WeakTab days={days} />}
      {tab === 'flagged' && <FlaggedTab />}
      {tab === 'changes' && <ChangesTab openGroup={setGroupId} />}
      {tab === 'sources' && <SourcesTab days={days} openGroup={setGroupId} />}
      {tab === 'near' && <NearMissesTab days={days} />}

      {sum && (
        <p className="mt-4 text-xs text-gray-400">
          Knowledge base: {sum.documents} documents ({sum.documentsFromSharePoint} synced from the ISO Hub){sum.documentsFailed ? ` · ${sum.documentsFailed} failed to load` : ''}.
          {' '}Answer pool: {sum.poolAnswers} saved answer{sum.poolAnswers === 1 ? '' : 's'} in use
          {sum.avgCostUsd ? `, saving ~${sum.avgCostUsd.toFixed(3)} each time one is reused` : ''}.
        </p>
      )}

      {groupId && <GroupPanel id={groupId} onClose={() => setGroupId(null)} />}
    </div>
  );
}

export default AssistantReview;
